/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import styles from './styles';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import { FilledInput, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { CancelPresentationRounded, SaveRounded, ScatterPlot } from '@mui/icons-material';
import ModalDialog from '../../ModalDialog';
import PropTypes from 'prop-types';
import useUnsavedChanges from '../../../../hooks/useUnsavedChanges';
import useUnitConversion from '../../../../hooks/useUnitConversion';
import { listObservation } from '../../../../store/actions/observation';
import { ANCHOR_POINT_TYPES, APP_ACTION_TYPES, HIDE_TARGET_ECD, MAIN_DEPTH_POINTS, TOOLTIPS } from '../../../../utils/constants';
import RunsECDSpan from '../RunsECDSpan';
import ControlsTable from '../ControlsTable';
import WellsObservationPointsModal from '../WellsObservationPointsModal';
import { checkPermissions } from '../../../../utils/checkPermissions';

const TargetModal = ({
  onCloseClick,
  isOpen,
  data,
  setTargetData,
  updateTargetData,
  onSave,
  anchorPoint,
  targetECD,
  targetData,
  setParameterDirty,
  runDetails,
  permissions
}) => {
  const classes = styles();
  const dispatch = useDispatch();
  const [properties, setProperties] = useState([]);
  const { unit } = useUnitConversion();
  const observation = useSelector((state) => state.observation);
  const [anchorType, setAnchorTypes] = useState(ANCHOR_POINT_TYPES);
  const [runsECDSpanNames, setRunsECDSpanNames] = useState(MAIN_DEPTH_POINTS);
  const [targetLoader, setTargetLoader] = useState(false);
  const [localAnchorPoint, setLocalAnchorPoint] = useState('None');
  const [localTargetECD, setLocalTargetECD] = useState(null);
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, onCloseClick);
  const RunsECDSpanUnits = useSelector((state) => state.hydraulics?.details?.units?.inputs?.ecd_span);
  const ControlsTableUnits = useSelector((state) => state.hydraulics?.details?.units?.inputs?.target_data?.controls_table);
  const [targetSaveEnable, setTargetSaveEnable] = useState(false);
  const [runsEcdDirty, setRunsEcdDirty] = useState(false);
  const [controlsTableDirty, setControlsTableDirty] = useState(false);
  const [wellsObservationPointsModal, setWellsObservationPointsModal] = useState(false);
  const hydraulicsDetails = permissions || useSelector((state) => state.hydraulics?.details);

  useEffect(() => {
    dispatch(listObservation(data?.wellsInfoId));
  }, [isOpen]);

  useEffect(() => {
    if (HIDE_TARGET_ECD.includes(localAnchorPoint)) {
      setLocalAnchorPoint('None');
      setLocalTargetECD(null);
    }
  }, [localAnchorPoint]);

  useEffect(() => {
    if (!observation.loading) {
      setLocalAnchorPoint(anchorPoint);
      setLocalTargetECD(targetECD);
    }
    if (observation?.data?.length && !observation.loading) {
      const extractObservationNames = observation.data.map(item => item.name);
      const updatedAnchorTypes = [...new Set([...ANCHOR_POINT_TYPES, ...extractObservationNames])];
      const updatedRunsECDSpanNames = [...new Set([...MAIN_DEPTH_POINTS, ...extractObservationNames])];
      setProperties(observation.data);
      setAnchorTypes(updatedAnchorTypes);
      setRunsECDSpanNames(updatedRunsECDSpanNames);
      setLocalTargetECD(targetECD);
      if (!updatedAnchorTypes.includes(anchorPoint)) {
        if (!observation?.data.some(item => item.isDirty === true)) {
          dispatch(
            enqueueSnackbar(
              'Current Anchor Point is not available in the list. Please update',
              'error',
              new Date().getTime() + Math.random()
            )
          );
        }
        setLocalAnchorPoint('');
        setLocalTargetECD('');
      } else {
        setLocalAnchorPoint(anchorPoint);
      }
    } else {
      setProperties([]);
    }
  }, [observation?.data, observation.loading]);

  const closeHandler = () => {
    if (unSavedExists || targetSaveEnable || runsEcdDirty || controlsTableDirty) {
      handleClose(false);
      return;
    }
    onCloseClick();
  };

  const getModalTitle = () => {
    return (
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          {`${data?.name}`}
        </Grid>
        <Grid item>
          <Tooltip title={TOOLTIPS.wellsObservationPoints}>
            <span>
              <IconButton
                onClick={() => {
                  setWellsObservationPointsModal(true);
                }}>
                <ScatterPlot fontSize='large' />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            variant="solid"
            disabled={!targetSaveEnable}
            onClick={() => {
              if ((!checkPermissions(APP_ACTION_TYPES.UPDATE, hydraulicsDetails?.permissions))) {
                dispatch(
                  enqueueSnackbar(
                    'You do not have permission to edit',
                    'error',
                    new Date().getTime() + Math.random()
                  )
                );
                return;
              }
              if (localAnchorPoint !== 'None' && targetData?.ecd_span?.length > 0 && !targetData.controls_table?.length) {
                dispatch(
                  enqueueSnackbar(
                    'Controls Table is also required when EMW Limits is specified',
                    'error',
                    new Date().getTime() + Math.random()
                  )
                );
                return;
              }
              const isNone = HIDE_TARGET_ECD.includes(localAnchorPoint);

              setTargetLoader(true);
              setTimeout(() => setTargetLoader(false), 500);

              let updatedTargetData = {};

              if (!isNone && !checkTargetECD()) {
                return;
              }

              if (!isNone) {
                updatedTargetData = {
                  anchor_point: localAnchorPoint,
                  target_ecd: Number(localTargetECD)
                };
              } else {
                updatedTargetData = {
                  anchor_point: localAnchorPoint,
                  target_ecd: null
                };
              }

              setTargetData(prev => {
                const shouldClearEcdSpan =
                  localAnchorPoint === 'None' &&
                  Array.isArray(prev.controls_table) &&
                  prev.controls_table.length === 0;

                return {
                  ...prev,
                  ...updatedTargetData,
                  ...(shouldClearEcdSpan && { ecd_span: [] })
                };
              });

              onCloseClick();
              // to set isDirty
              updateTargetData && updateTargetData(data);
              onSave(localAnchorPoint, localTargetECD);
            }}
          >
            <SaveRounded fontSize='large' />
          </IconButton>
          <IconButton
            onClick={closeHandler}
          >
            <CancelPresentationRounded fontSize='large' />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const checkTargetECD = () => {
    const minECD = observation?.units?.target_data?.target_ecd?.min;
    const maxECD = observation?.units?.target_data?.target_ecd?.max;
    const unit = observation?.units?.target_data?.target_ecd?.label;

    const isAnchorPointEmpty = localAnchorPoint === '' || localAnchorPoint === null || localAnchorPoint === undefined;
    const isTargetECDEmpty = localTargetECD === '' || localTargetECD === null || localTargetECD === undefined;

    if (isAnchorPointEmpty && isTargetECDEmpty) {
      dispatch(
        enqueueSnackbar(
          'Please select Anchor Point and enter Target ECD',
          'error',
          Date.now() + Math.random()
        )
      );
      return false;
    }

    if (isAnchorPointEmpty) {
      dispatch(
        enqueueSnackbar(
          'Please choose Anchor Point',
          'error',
          Date.now() + Math.random()
        )
      );
      return false;
    }

    if (isTargetECDEmpty) {
      dispatch(
        enqueueSnackbar(
          'Please enter Target ECD',
          'error',
          Date.now() + Math.random()
        )
      );
      return false;
    }

    const targetECDValue = Number(localTargetECD);
    if (isNaN(targetECDValue) || targetECDValue < minECD || targetECDValue > maxECD) {
      dispatch(
        enqueueSnackbar(
          `Target ECD must be a number between ${minECD} and ${maxECD} ${unit}`,
          'error',
          Date.now() + Math.random()
        )
      );
      return false;
    }

    return true;
  };

  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={closeHandler}
      dialogClass={clsx(classes.dialogModal, localAnchorPoint === 'None' ? classes.dialogMinWidth : classes.dialogMaxWidth)}
    >
      <Grid item className={classes.targetContainer} maxWidth='755px'>
        <Grid container direction="column" margin={'6px'}>
          <Grid item>
            <Typography variant="h5">Run&apos;s Target Settings</Typography>
          </Grid>
          <Grid container sx={{ margin: '30px 0' }}>
            <Grid flex={1} container item alignItems="center">
            <Grid item xs={3}>
              <Typography fontWeight="700">Anchor Point</Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth variant="filled" disabled={observation.loading || targetLoader || runsEcdDirty || controlsTableDirty || !checkPermissions(APP_ACTION_TYPES.UPDATE, hydraulicsDetails?.permissions)}>
                <InputLabel id="anchor-point-label">Select Anchor Point</InputLabel>
                <Select
                  labelId="anchor-point-label"
                  id="anchor-point-select"
                  value={localAnchorPoint}
                  onChange={(e) => {
                    setLocalAnchorPoint(e.target.value);
                    setTargetSaveEnable(true);
                  }}
                  disableUnderline
                >
                  {anchorType?.map((item, index) => (
                    <MenuItem key={`item_${item}_${index}`} value={item}>{item}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid flex={1} container item alignItems="center">
            <Grid item xs={4}>
              {!HIDE_TARGET_ECD.includes(localAnchorPoint) && (
                <Typography fontWeight="700">
                  Target ECD ({observation?.units?.target_data?.target_ecd?.label})
                </Typography>
              )}
            </Grid>
            <Grid item xs={6}>
              {!HIDE_TARGET_ECD.includes(localAnchorPoint) && (
                <FormControl fullWidth variant="filled" disabled={observation.loading || targetLoader || runsEcdDirty || controlsTableDirty || !checkPermissions(APP_ACTION_TYPES.UPDATE, hydraulicsDetails?.permissions)}>
                  <InputLabel htmlFor="target-ecd">Enter Target ECD</InputLabel>
                  <FilledInput
                    id="target-ecd"
                    value={localTargetECD || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        setLocalTargetECD(value);
                        setTargetSaveEnable(true);
                      }
                    }}
                    disableUnderline
                  />
                </FormControl>
              )}
            </Grid>
          </Grid>
          </Grid>
        </Grid>
      </Grid>
      {localAnchorPoint !== 'None' && !observation.loading
        ? (
          <>
            <RunsECDSpan
              units={RunsECDSpanUnits}
              currentUnit={unit}
              runsECDSpanNames={runsECDSpanNames}
              loading={observation.loading}
              targetData={targetData}
              setTargetData={setTargetData}
              setParameterDirty={setParameterDirty}
              setTargetSaveEnable={setTargetSaveEnable}
              setRunsEcdDirty={setRunsEcdDirty}
              controlsTableDirty={controlsTableDirty}
              permissions={hydraulicsDetails?.permissions}
            />
            {targetData?.ecd_span?.length > 0
              ? (
                <ControlsTable
                  units={ControlsTableUnits}
                  currentUnit={unit}
                  runsECDSpanNames={runsECDSpanNames}
                  loading={observation.loading}
                  targetData={targetData}
                  setTargetData={setTargetData}
                  setParameterDirty={setParameterDirty}
                  setTargetSaveEnable={setTargetSaveEnable}
                  setControlsTableDirty={setControlsTableDirty}
                  runsEcdDirty={runsEcdDirty}
                  permissions={hydraulicsDetails?.permissions}
                />
                )
              : null}
          </>
          )
        : null}
      <UnsavedChangesModal />
      {wellsObservationPointsModal
        ? (
          <WellsObservationPointsModal
            data={data}
            isOpen={wellsObservationPointsModal}
            onCloseClick={() => {
              setWellsObservationPointsModal(false);
            }}
            anchorPoint={localAnchorPoint}
            runDetails={runDetails}
          />
          )
        : null}
    </ModalDialog>
  );
};
TargetModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  selectedJob: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.any,
  setTargetData: PropTypes.func,
  updateTargetData: PropTypes.func,
  anchorPoint: PropTypes.any,
  targetECD: PropTypes.any,
  onSave: PropTypes.func,
  targetData: PropTypes.any,
  setParameterDirty: PropTypes.func,
  runDetails: PropTypes.any,
  permissions: PropTypes.object
};
TargetModal.defaultProps = {
  selectedJob: {}
};

export default TargetModal;
